import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, signal } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { CheckboxStateService } from '../../../core-module/services/state/checkbox-state.service';
import { FeatureDefaultsStateService } from '../../../core-module/services/state/feature-defaults-state.service';
import { InputErrorsStateService } from '../../../core-module/services/state/input-errors-state.service';

interface DipswitchSetting {
  key: string;
  index: number;
  min: number;
  max: number;
  default: number;
  value: number;
  displayValue: LegacyAny;
}

@Component({
  standalone: false,
  selector: 'app-dipswitch',
  templateUrl: './dipswitch.html',
  styleUrls: ['./dipswitch.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DipswitchComponent extends FieldType implements OnInit, OnDestroy {
  settings: DipswitchSetting[] = [];

  inputError: LegacyAny = {};
  private readonly dipswitchDisabledButtonSignal = signal(false);

  private readonly defaultColorSignal = signal(true);

  private readonly isIncludedInConfigurationSignal = signal(false);
  private ignoreParentUpdate = false;
  private hasConfigData = false;
  private isResetting = false;

  private readonly settingInputSubject = new Subject<{ key: string; value: string }>();

  private properties: LegacyAny;
  private readonly destroy$ = new Subject<void>();

  get dipswitchDisabledButton(): boolean { return this.dipswitchDisabledButtonSignal(); }
  set dipswitchDisabledButton(value: boolean) { this.dipswitchDisabledButtonSignal.set(!!value); }

  get defaultColor(): boolean { return this.defaultColorSignal(); }
  set defaultColor(value: boolean) { this.defaultColorSignal.set(!!value); }

  get isIncludedInConfiguration(): boolean { return this.isIncludedInConfigurationSignal(); }
  set isIncludedInConfiguration(value: boolean) { this.isIncludedInConfigurationSignal.set(!!value); }

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private readonly featureDefaults: FeatureDefaultsStateService,
    private readonly checkboxState: CheckboxStateService,
    private readonly inputErrors: InputErrorsStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ignoreParentUpdate = true;

    this.properties = (this.field as LegacyAny)?.properties;

    this.loadFromSchema();

    let savedValue = null;

    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element['featureName'] && element['featureName'].toString().toUpperCase() === 'DIPSWITCH') {
          isConfigEdited = true;
          savedValue = element['featureParameters'][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    if (!isConfigEdited) {
      const dipswitchData = this.readStoredFeatureData();
      if (dipswitchData) {
        savedValue = dipswitchData;
        this.hasConfigData = Object.keys(savedValue).length > 0;
      }
    }

    const currentValue = savedValue || this.formControl.value || {};
    if (currentValue && Object.keys(currentValue).length > 0) {
      this.loadDataFromObject(currentValue);
    }

    this.setupInputDebouncing();

    this.featureDefaults.getDipswitchDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === 'valid') {
          this.defaultColor = true;
          this.resetToDefaults();
          this.featureDefaults.setDipswitchDefaultValue('');
        }
      });

    this.checkboxState.getDipswitchParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          if (!state.checked) {
            this.hasConfigData = false;
            this.inputErrors.setDipswitchInputError.next(false);
            this.ref.detectChanges();
            return;
          }

          if (this.hasConfigData) {
            this.sendErrorStateToParent();
            return;
          }

          this.updateFormControl();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    this.revalidateAllFields();

    this.checkDefaults();

    if (!this.defaultColor) {
      this.checkboxState.enableDipswitchCheckbox();
      this.isIncludedInConfiguration = true;
      this.hasConfigData = true;
    }

    this.sendErrorStateToParent();

    this.ignoreParentUpdate = false;
  }

  // Only the required settings are configurable; the rest are schema constants.
  // Count varies by version: 4 of 8 settings (3 dipswitches) or more (4 dipswitches).
  private loadFromSchema(): void {
    if (!this.properties) {
      return;
    }

    const requiredKeys: string[] = (this.field as LegacyAny)?.requiredProperties ?? [];

    this.settings = requiredKeys
      .map((key) => ({ key, match: /^CurrentAtDIPSwitchSetting(\d+)$/.exec(key) }))
      .filter((entry) => entry.match && this.properties[entry.key])
      .map((entry) => {
        const prop = this.properties[entry.key];
        const min = prop.minimum ?? 0;
        const max = prop.maximum ?? 0;
        const defaultValue = prop.default ?? min;
        return {
          key: entry.key,
          index: Number(entry.match[1]),
          min,
          max,
          default: defaultValue,
          value: defaultValue,
          displayValue: defaultValue,
        };
      })
      .sort((a, b) => a.index - b.index);
  }

  // Device reads store the payload under the backend's featureName casing.
  private readStoredFeatureData(): LegacyAny {
    for (const key of ['Dipswitch', 'dipswitch', 'DIPSWITCH', 'DipSwitch']) {
      const data = this.appStateService.getFeatureData(key);
      if (data && Object.keys(data).length > 0) {
        return data;
      }
    }
    return null;
  }

  private loadDataFromObject(data: LegacyAny): void {
    this.settings.forEach((setting) => {
      if (data[setting.key] !== undefined) {
        setting.value = data[setting.key];
        setting.displayValue = data[setting.key];
      }
    });
  }

  private setupInputDebouncing(): void {
    this.settingInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(({ key, value }) => {
      this.validateAndUpdateField(key, value);
    });
  }

  // Auto-select "Include in configuration" on user edits, and clear the pending
  // request once the values are back to default so it cannot re-select later.
  private syncIncludeInConfigurationRequest(): void {
    if (!this.defaultColor) {
      this.checkboxState.enableDipswitchCheckbox();
      this.isIncludedInConfiguration = true;
    } else if (!this.isIncludedInConfiguration) {
      this.checkboxState.disableDipswitchCheckbox();
    }
  }

  onFieldInput(inputEl: HTMLInputElement, key: string): void {
    this.settingInputSubject.next({ key, value: this.sanitizeInteger(inputEl) });
  }

  // Pasting bypasses the keypress filter, and the [value] binding does not repaint
  // when the parsed number is unchanged, so correct the DOM value in place.
  private sanitizeInteger(inputEl: HTMLInputElement): string {
    const integerPart = /^-?\d*/.exec(inputEl.value)[0];
    if (integerPart !== inputEl.value || inputEl.validity?.badInput) {
      inputEl.value = integerPart;
    }
    return integerPart;
  }

  private validateAndUpdateField(key: string, value: string): void {
    const setting = this.findSetting(key);
    if (this.isResetting || !setting) {
      return;
    }

    this.ignoreParentUpdate = true;
    const numValue = parseInt(value, 10);

    setting.value = numValue;
    setting.displayValue = numValue;

    this.inputError[key] =
      value === '' || value === '-0' || isNaN(numValue) || numValue < setting.min || numValue > setting.max;

    this.checkDefaults();
    this.syncIncludeInConfigurationRequest();

    this.recalcErrorState();
    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.inputErrors.setDipswitchInputError.next(this.dipswitchDisabledButton);
    } else {
      this.inputErrors.setDipswitchInputError.next(false);
    }
  }

  private revalidateAllFields(): void {
    this.dipswitchDisabledButton = false;
    this.inputError = {};

    this.settings.forEach((setting) => {
      // null means NaN was saved to storage (JSON converts NaN to null)
      if (
        setting.value === null || setting.value === undefined || isNaN(setting.value) ||
        setting.value < setting.min || setting.value > setting.max
      ) {
        this.inputError[setting.key] = true;
        this.dipswitchDisabledButton = true;
      }
    });

    this.sendErrorStateToParent();
  }

  private recalcErrorState(): void {
    this.dipswitchDisabledButton = false;
    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.dipswitchDisabledButton = true;
        break;
      }
    }
  }

  checkDefaults(): void {
    this.defaultColor = this.settings.every((setting) => setting.value === setting.default);
    this.featureDefaults.setDipswitchDefaultColor(this.defaultColor);
  }

  resetToDefaults(): void {
    this.isResetting = true;

    const wasIncludedInConfiguration = this.isIncludedInConfiguration;

    this.settings.forEach((setting) => {
      setting.value = setting.default;
      setting.displayValue = setting.default;
    });
    this.inputError = {};
    this.dipswitchDisabledButton = false;
    this.defaultColor = true;

    this.updateFormControl();
    this.featureDefaults.setDipswitchDefaultColor(this.defaultColor);

    this.inputErrors.setDipswitchInputError.next(false);

    if (wasIncludedInConfiguration) {
      this.isIncludedInConfiguration = true;
      this.checkboxState.enableDipswitchCheckbox();
    }

    this.ref.detectChanges();

    // Release resetting flag after debounce period to discard any pending values
    setTimeout(() => {
      this.isResetting = false;
    }, 150);
  }

  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }
    const value: LegacyAny = {};
    this.settings.forEach((setting) => {
      value[setting.key] = setting.value;
    });
    this.formControl.setValue(value);
    this.setConfigurationData(value, 'Dipswitch');
  }

  private setConfigurationData(value: LegacyAny, keyVal: string): void {
    if (value != null) {
      this.appStateService.setFeatureData(keyVal, Object.assign({}, value));
    }
  }

  private findSetting(key: string): DipswitchSetting | undefined {
    return this.settings.find((setting) => setting.key === key);
  }

  // Normalize input value on blur (e.g. "007" -> "7", force DOM update)
  onFieldBlur(inputEl: HTMLInputElement, key: string): void {
    const numericValue = parseInt(this.sanitizeInteger(inputEl), 10);
    const cleaned = isNaN(numericValue) ? '' : String(numericValue);
    inputEl.value = '';
    inputEl.value = cleaned;
    const setting = this.findSetting(key);
    if (setting) {
      setting.displayValue = isNaN(numericValue) ? '' : numericValue;
    }
  }

  numberOnly(event: LegacyAny): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
